/**
 * RFQ (Request for Quotation) API Module - CR-1403
 *
 * Provides API methods for the RFQ workflow including:
 * - RFQ CRUD operations
 * - Supplier response management
 * - Query/communication threads
 * - NDA management
 * - Response comparison
 *
 * @module rfq
 */
import api, { type AxiosResponse, type PaginatedResponse } from './client';
import type {
  RFQ,
  RFQListItem,
  RFQLineItem,
  RFQDocument,
  RFQQuantityBreak,
  RFQAdditionalCost,
  RFQQuery,
  RFQQueryMessage,
  RFQNDA,
  RFQActivityLog,
  SupplierResponse,
  SupplierResponseListItem,
  SupplierResponseLineItem,
  SupplierResponseQuantityBreak,
  SupplierResponseAdditionalCost,
  SupplierCertificateSubmission,
  RequiredCertificate,
  RFQComparison,
  RFQDashboardStats,
  QueryParams,
} from './types';

// ============================================================================
// Types for API requests
// ============================================================================

export interface SendToSuppliersRequest {
  supplier_ids: number[];
}

export interface AwardRFQRequest {
  supplier_id: number;
  contract_valid_from: string;
  contract_valid_to: string;
}

export interface CancelRFQRequest {
  reason?: string;
}

/** CR-5461: server-persisted RFQ wizard draft. wizard_state is shaped by
 * the frontend (rfqWizardTypes.ts) — the backend stores it as-is and
 * does not validate the JSON shape; final validation happens at submit. */
export interface RFQWizardDraft {
  wizard_state: Record<string, unknown>;
  updated_at: string;
}

/** CR-5466: admin-managed RFQ Type — per-company values used as the
 *  dropdown source on the Create RFQ wizard. */
export interface RFQType {
  id: number;
  name: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Matches RFQQueryCreateSerializer exactly.
 *
 * This used to declare `rfq` and an optional `supplier_id`. The endpoint has
 * always required `rfq_id` and a mandatory `supplier_id`, so every query
 * creation was rejected 400 — the whole RFQ Q&A thread, on both sides.
 * `supplier_id` is required rather than inferred because a customer can hold a
 * thread with any one of the invited bidders, and the server cannot guess which.
 */
export interface CreateQueryRequest {
  rfq_id: number;
  supplier_id: number;
  subject: string;
  message: string;
  attachment_ids?: number[];
}

export interface RespondToQueryRequest {
  message: string;
  attachment_ids?: number[];
}

export interface CreateRFQRequest {
  // CR-5466: title is no longer surfaced in the wizard, but the model
  // accepts blank — caller may omit it. The wizard derives a sensible
  // default from reference or description on submit.
  title?: string;
  description: string;
  // CR-5468: customer is optional from the FE — backend stamps it from
  // the requester's active customer-portal company. Helix admin endpoints
  // pass it explicitly; customer-portal wizard does not.
  customer?: number;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: 'materials' | 'services' | 'equipment' | 'other';
  contract_type?: 'one_time' | 'regular_running';
  contract_frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  nda_required?: boolean;
  auto_create_contract?: boolean;
  currency?: string;
  total_estimated_value?: number;
  // CR-5466: Sarah's design separates issue date from response deadline
  issue_date?: string;
  response_deadline: string;
  validity_period_days?: number;
  terms_and_conditions?: string;
  special_requirements?: string;
  assigned_to?: number;
  // CR-5466
  reference?: string;
  rfq_type?: number | null;
  // CR-6203: header-level delivery point and need-by date
  delivery_location?: string;
  required_delivery_date?: string | null;
}

export interface UpdateRFQRequest extends Partial<CreateRFQRequest> {}

export interface CreateLineItemRequest {
  rfq: number;
  line_number: number;
  item_description: string;
  part_number?: string;
  quantity: number;
  unit_of_measure?: string;
  target_unit_price?: number;
  delivery_location?: string;
  required_delivery_date?: string;
  drawing_issue?: string;  // CR-5467
  technical_specifications?: string;
  notes?: string;
  has_quantity_breaks?: boolean;
}

export interface CreateQuantityBreakRequest {
  rfq_line: number;
  from_quantity: number;
  to_quantity?: number;
  unit_price?: number;
  lot_price?: number;
  is_unit_pricing?: boolean;
}

export interface CreateAdditionalCostRequest {
  rfq: number;
  cost_type: 'tooling' | 'packaging' | 'transport' | 'setup' | 'certification' | 'other';
  description: string;
  amount: number;
  currency?: string;
  notes?: string;
}

export interface CreateResponseLineItemRequest {
  response: number;
  rfq_line_item: number;
  unit_price: number;
  lead_time_days?: number;
  /** Unit the price is per when it differs from the RFQ line's UOM. */
  quoted_uom?: string;
  alternative_offered?: string;
  notes?: string;
  has_quantity_breaks?: boolean;
}

/** Body of the customer's request for a revised quote. */
export interface RequestRevisionRequest {
  message: string;
  target_total?: number | null;
  target_lead_time_days?: number | null;
}

export interface CreateResponseQuantityBreakRequest {
  response_line: number;
  from_quantity: number;
  to_quantity?: number;
  unit_price?: number;
  lot_price?: number;
}

export interface CreateResponseAdditionalCostRequest {
  response: number;
  cost_type: 'tooling' | 'packaging' | 'transport' | 'setup' | 'certification' | 'other';
  description: string;
  amount: number;
  currency?: string;
  notes?: string;
}

// ============================================================================
// RFQ API
// ============================================================================

export const rfqAPI = {
  // ==================== RFQ CRUD ====================

  /**
   * List RFQs with optional filters
   */
  getRFQs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RFQListItem>>> =>
    api.get('/rfq/rfqs/', { params }),

  /**
   * Get RFQ details by ID
   */
  getRFQ: (id: number): Promise<AxiosResponse<RFQ>> =>
    api.get(`/rfq/rfqs/${id}/`),

  /**
   * Create a new RFQ
   */
  createRFQ: (data: CreateRFQRequest): Promise<AxiosResponse<RFQ>> =>
    api.post('/rfq/rfqs/', data),

  /**
   * Update an existing RFQ
   */
  updateRFQ: (id: number, data: UpdateRFQRequest): Promise<AxiosResponse<RFQ>> =>
    api.patch(`/rfq/rfqs/${id}/`, data),

  /**
   * Delete an RFQ
   */
  deleteRFQ: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/rfqs/${id}/`),

  /**
   * Get RFQ dashboard statistics
   */
  getDashboardStats: (): Promise<AxiosResponse<RFQDashboardStats>> =>
    api.get('/rfq/rfqs/dashboard_stats/'),

  // ==================== CR-5461 RFQ Wizard Draft ====================

  /**
   * Get the current user's in-progress RFQ wizard draft.
   *
   * Returns 204 No Content when the user has no saved draft — callers
   * should check `response.status === 204` and treat that as "no draft".
   */
  getDraft: (): Promise<AxiosResponse<RFQWizardDraft | null>> =>
    api.get('/rfq/drafts/me/'),

  /**
   * Upsert the current user's RFQ wizard draft.
   *
   * Idempotent — body replaces stored wizard_state. Called debounced by
   * the auto-save hook on every wizard state change.
   */
  saveDraft: (wizardState: Record<string, unknown>): Promise<AxiosResponse<RFQWizardDraft>> =>
    api.put('/rfq/drafts/me/', { wizard_state: wizardState }),

  /**
   * Clear the current user's RFQ wizard draft.
   *
   * Called by the wizard after a successful RFQ submit so the resume
   * banner doesn't appear next time.
   */
  deleteDraft: (): Promise<AxiosResponse<void>> =>
    api.delete('/rfq/drafts/me/'),

  // ==================== CR-5466 RFQ Type (admin-managed) ====================

  /** List RFQ Types for the active company (enabled + disabled). */
  getRFQTypes: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RFQType> | RFQType[]>> =>
    api.get('/rfq/types/', { params }),

  /** Admin: create a new RFQ Type. */
  createRFQType: (data: { name: string; enabled?: boolean }): Promise<AxiosResponse<RFQType>> =>
    api.post('/rfq/types/', data),

  /** Admin: update an RFQ Type (rename or toggle enabled). */
  updateRFQType: (id: number, data: { name?: string; enabled?: boolean }): Promise<AxiosResponse<RFQType>> =>
    api.patch(`/rfq/types/${id}/`, data),

  /** Admin: delete an RFQ Type (PROTECT on FK; only allowed when unreferenced). */
  deleteRFQType: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/types/${id}/`),

  // ==================== RFQ Workflow Actions ====================

  /**
   * Send RFQ to selected suppliers
   */
  sendToSuppliers: (rfqId: number, data: SendToSuppliersRequest): Promise<AxiosResponse<RFQ>> =>
    api.post(`/rfq/rfqs/${rfqId}/send_to_suppliers/`, data),

  /**
   * Award RFQ to winning supplier
   */
  awardRFQ: (rfqId: number, data: AwardRFQRequest): Promise<AxiosResponse<RFQ>> =>
    api.post(`/rfq/rfqs/${rfqId}/award/`, data),

  /**
   * Cancel an RFQ
   */
  cancelRFQ: (rfqId: number, data: CancelRFQRequest): Promise<AxiosResponse<RFQ>> =>
    api.post(`/rfq/rfqs/${rfqId}/cancel/`, data),

  /**
   * Compare supplier responses for an RFQ
   */
  compareResponses: (rfqId: number): Promise<AxiosResponse<RFQComparison>> =>
    api.get(`/rfq/rfqs/${rfqId}/compare_responses/`),

  /** The same comparison as an .xlsx (Suppliers + Line Items sheets). */
  exportComparison: (rfqId: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/rfq/rfqs/${rfqId}/export_comparison/`, { responseType: 'blob' }),

  // ==================== Line Items ====================

  /**
   * List RFQ line items
   */
  getLineItems: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RFQLineItem>>> =>
    api.get('/rfq/line-items/', { params }),

  /**
   * Get line item details
   */
  getLineItem: (id: number): Promise<AxiosResponse<RFQLineItem>> =>
    api.get(`/rfq/line-items/${id}/`),

  /**
   * Create a line item
   */
  createLineItem: (data: CreateLineItemRequest): Promise<AxiosResponse<RFQLineItem>> =>
    api.post('/rfq/line-items/', data),

  /**
   * Update a line item
   */
  updateLineItem: (id: number, data: Partial<CreateLineItemRequest>): Promise<AxiosResponse<RFQLineItem>> =>
    api.patch(`/rfq/line-items/${id}/`, data),

  /**
   * Delete a line item
   */
  deleteLineItem: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/line-items/${id}/`),

  // ==================== Quantity Breaks ====================

  /**
   * List quantity breaks for a line item
   */
  getQuantityBreaks: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RFQQuantityBreak>>> =>
    api.get('/rfq/quantity-breaks/', { params }),

  /**
   * Create a quantity break
   */
  createQuantityBreak: (data: CreateQuantityBreakRequest): Promise<AxiosResponse<RFQQuantityBreak>> =>
    api.post('/rfq/quantity-breaks/', data),

  /**
   * Update a quantity break
   */
  updateQuantityBreak: (id: number, data: Partial<CreateQuantityBreakRequest>): Promise<AxiosResponse<RFQQuantityBreak>> =>
    api.patch(`/rfq/quantity-breaks/${id}/`, data),

  /**
   * Delete a quantity break
   */
  deleteQuantityBreak: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/quantity-breaks/${id}/`),

  // ==================== Additional Costs ====================

  /**
   * List additional costs for an RFQ
   */
  getAdditionalCosts: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RFQAdditionalCost>>> =>
    api.get('/rfq/additional-costs/', { params }),

  /**
   * Create an additional cost
   */
  createAdditionalCost: (data: CreateAdditionalCostRequest): Promise<AxiosResponse<RFQAdditionalCost>> =>
    api.post('/rfq/additional-costs/', data),

  /**
   * Update an additional cost
   */
  updateAdditionalCost: (id: number, data: Partial<CreateAdditionalCostRequest>): Promise<AxiosResponse<RFQAdditionalCost>> =>
    api.patch(`/rfq/additional-costs/${id}/`, data),

  /**
   * Delete an additional cost
   */
  deleteAdditionalCost: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/additional-costs/${id}/`),

  // ==================== Documents ====================

  /**
   * List RFQ documents
   */
  getDocuments: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RFQDocument>>> =>
    api.get('/rfq/documents/', { params }),

  /**
   * Upload a document
   */
  uploadDocument: (data: FormData): Promise<AxiosResponse<RFQDocument>> =>
    api.post('/rfq/documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /**
   * Download a document.
   *
   * `/media/rfq/**` is hard-blocked server-side (private media), so the
   * serialized `file` URL is not fetchable — anything showing an RFQ document
   * to a user must come through this authenticated endpoint.
   */
  downloadDocument: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/rfq/documents/${id}/download/`, { responseType: 'blob' }),

  /**
   * Delete a document
   */
  deleteDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/documents/${id}/`),

  // ==================== Required Certificates ====================

  /**
   * List required certificates for an RFQ
   */
  getRequiredCertificates: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RequiredCertificate>>> =>
    api.get('/rfq/certificates/', { params }),

  /**
   * Create a required certificate
   */
  createRequiredCertificate: (data: {
    rfq: number;
    certificate_type: string;
    certificate_name: string;
    description?: string;
    is_mandatory?: boolean;
    expiry_tolerance_days?: number;
  }): Promise<AxiosResponse<RequiredCertificate>> =>
    api.post('/rfq/certificates/', data),

  /**
   * Delete a required certificate
   */
  deleteRequiredCertificate: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/certificates/${id}/`),

  // ==================== Queries ====================

  /**
   * List RFQ queries
   */
  getQueries: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RFQQuery>>> =>
    api.get('/rfq/queries/', { params }),

  /**
   * Get query details
   */
  getQuery: (id: number): Promise<AxiosResponse<RFQQuery>> =>
    api.get(`/rfq/queries/${id}/`),

  /**
   * Create a new query
   */
  createQuery: (data: CreateQueryRequest): Promise<AxiosResponse<RFQQuery>> =>
    api.post('/rfq/queries/', data),

  /**
   * Respond to a query.
   *
   * `attachmentIds` are RFQDocument ids already uploaded via `uploadDocument`;
   * the endpoint resolves them onto the message's `attachments` M2M. Omitted
   * entirely when empty so the payload stays identical to a plain reply.
   */
  respondToQuery: (
    queryId: number,
    message: string,
    attachmentIds?: number[],
  ): Promise<AxiosResponse<RFQQueryMessage>> =>
    api.post(`/rfq/queries/${queryId}/respond/`, {
      message,
      ...(attachmentIds && attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
    }),

  /**
   * Close a query
   */
  closeQuery: (queryId: number): Promise<AxiosResponse<RFQQuery>> =>
    api.post(`/rfq/queries/${queryId}/close/`),

  // ==================== Query Messages (CR-2220) ====================

  /**
   * CR-2220: Delete a query message (soft delete)
   */
  deleteQueryMessage: (messageId: number): Promise<AxiosResponse<RFQQueryMessage>> =>
    api.post(`/rfq/query-messages/${messageId}/delete_message/`),

  /**
   * CR-2220: Restore a deleted query message
   */
  restoreQueryMessage: (messageId: number): Promise<AxiosResponse<RFQQueryMessage>> =>
    api.post(`/rfq/query-messages/${messageId}/restore_message/`),

  /**
   * CR-2220: Strikethrough a query message
   */
  strikethroughQueryMessage: (messageId: number): Promise<AxiosResponse<RFQQueryMessage>> =>
    api.post(`/rfq/query-messages/${messageId}/strikethrough/`),

  /**
   * CR-2220: Remove strikethrough from a query message
   */
  removeStrikethroughQueryMessage: (messageId: number): Promise<AxiosResponse<RFQQueryMessage>> =>
    api.post(`/rfq/query-messages/${messageId}/remove_strikethrough/`),

  // ==================== NDAs ====================

  /**
   * List RFQ NDAs
   */
  getNDAs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RFQNDA>>> =>
    api.get('/rfq/ndas/', { params }),

  /**
   * Get NDA details
   */
  getNDA: (id: number): Promise<AxiosResponse<RFQNDA>> =>
    api.get(`/rfq/ndas/${id}/`),

  /**
   * Accept an NDA
   */
  acceptNDA: (ndaId: number): Promise<AxiosResponse<RFQNDA>> =>
    api.post(`/rfq/ndas/${ndaId}/accept/`),

  /**
   * Decline an NDA
   */
  declineNDA: (ndaId: number): Promise<AxiosResponse<RFQNDA>> =>
    api.post(`/rfq/ndas/${ndaId}/decline/`),

  // ==================== Supplier Responses ====================

  /**
   * List supplier responses
   */
  getResponses: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<SupplierResponseListItem>>> =>
    api.get('/rfq/responses/', { params }),

  /**
   * Get response details
   */
  getResponse: (id: number): Promise<AxiosResponse<SupplierResponse>> =>
    api.get(`/rfq/responses/${id}/`),

  /**
   * Update a supplier response
   */
  updateResponse: (id: number, data: Partial<{
    currency: string;
    validity_period_days: number;
    lead_time_days: number;
    payment_terms: string;
    delivery_terms: string;
    notes: string;
  }>): Promise<AxiosResponse<SupplierResponse>> =>
    api.patch(`/rfq/responses/${id}/`, data),

  /**
   * Submit a supplier response
   */
  submitResponse: (responseId: number): Promise<AxiosResponse<SupplierResponse>> =>
    api.post(`/rfq/responses/${responseId}/submit/`),

  /**
   * Customer asks the supplier for a revised quote on a submitted response.
   * The response reopens for editing; the current quote is kept as history.
   */
  requestRevision: (responseId: number, data: RequestRevisionRequest): Promise<AxiosResponse<SupplierResponse>> =>
    api.post(`/rfq/responses/${responseId}/request_revision/`, data),

  /**
   * Accept NDA for a response
   */
  acceptResponseNDA: (responseId: number): Promise<AxiosResponse<SupplierResponse>> =>
    api.post(`/rfq/responses/${responseId}/accept_nda/`),

  /**
   * Decline NDA for a response
   */
  declineResponseNDA: (responseId: number): Promise<AxiosResponse<SupplierResponse>> =>
    api.post(`/rfq/responses/${responseId}/decline_nda/`),

  // ==================== Response Line Items ====================

  /**
   * List response line items
   */
  getResponseLineItems: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<SupplierResponseLineItem>>> =>
    api.get('/rfq/response-lines/', { params }),

  /**
   * Create a response line item
   */
  createResponseLineItem: (data: CreateResponseLineItemRequest): Promise<AxiosResponse<SupplierResponseLineItem>> =>
    api.post('/rfq/response-lines/', data),

  /**
   * Update a response line item
   */
  updateResponseLineItem: (id: number, data: Partial<CreateResponseLineItemRequest>): Promise<AxiosResponse<SupplierResponseLineItem>> =>
    api.patch(`/rfq/response-lines/${id}/`, data),

  /**
   * Delete a response line item
   */
  deleteResponseLineItem: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/response-lines/${id}/`),

  // ==================== Response Quantity Breaks ====================

  /**
   * List response quantity breaks
   */
  getResponseQuantityBreaks: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<SupplierResponseQuantityBreak>>> =>
    api.get('/rfq/response-quantity-breaks/', { params }),

  /**
   * Create a response quantity break
   */
  createResponseQuantityBreak: (data: CreateResponseQuantityBreakRequest): Promise<AxiosResponse<SupplierResponseQuantityBreak>> =>
    api.post('/rfq/response-quantity-breaks/', data),

  /**
   * Update a response quantity break
   */
  updateResponseQuantityBreak: (id: number, data: Partial<CreateResponseQuantityBreakRequest>): Promise<AxiosResponse<SupplierResponseQuantityBreak>> =>
    api.patch(`/rfq/response-quantity-breaks/${id}/`, data),

  /**
   * Delete a response quantity break
   */
  deleteResponseQuantityBreak: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/response-quantity-breaks/${id}/`),

  // ==================== Response Additional Costs ====================

  /**
   * List response additional costs
   */
  getResponseAdditionalCosts: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<SupplierResponseAdditionalCost>>> =>
    api.get('/rfq/response-additional-costs/', { params }),

  /**
   * Create a response additional cost
   */
  createResponseAdditionalCost: (data: CreateResponseAdditionalCostRequest): Promise<AxiosResponse<SupplierResponseAdditionalCost>> =>
    api.post('/rfq/response-additional-costs/', data),

  /**
   * Update a response additional cost
   */
  updateResponseAdditionalCost: (id: number, data: Partial<CreateResponseAdditionalCostRequest>): Promise<AxiosResponse<SupplierResponseAdditionalCost>> =>
    api.patch(`/rfq/response-additional-costs/${id}/`, data),

  /**
   * Delete a response additional cost
   */
  deleteResponseAdditionalCost: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/response-additional-costs/${id}/`),

  // ==================== Certificate Submissions ====================

  /**
   * List certificate submissions
   */
  /**
   * Download a supplier's certificate file. Private media — the serialized
   * `certificate_file` URL is blocked server-side, so use this endpoint.
   */
  downloadCertificateSubmission: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/rfq/certificate-submissions/${id}/download/`, { responseType: 'blob' }),

  getCertificateSubmissions: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<SupplierCertificateSubmission>>> =>
    api.get('/rfq/certificate-submissions/', { params }),

  /**
   * Upload a certificate submission
   */
  uploadCertificateSubmission: (data: FormData): Promise<AxiosResponse<SupplierCertificateSubmission>> =>
    api.post('/rfq/certificate-submissions/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /**
   * Verify a certificate submission
   */
  verifyCertificateSubmission: (id: number): Promise<AxiosResponse<SupplierCertificateSubmission>> =>
    api.post(`/rfq/certificate-submissions/${id}/verify/`),

  /**
   * Reject a certificate submission
   */
  rejectCertificateSubmission: (id: number, data: { reason: string }): Promise<AxiosResponse<SupplierCertificateSubmission>> =>
    api.post(`/rfq/certificate-submissions/${id}/reject/`, data),

  /**
   * Delete a certificate submission
   */
  deleteCertificateSubmission: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rfq/certificate-submissions/${id}/`),

  // ==================== Activity Log ====================

  /**
   * List activity log entries for an RFQ
   */
  getActivityLog: (rfqId: number): Promise<AxiosResponse<PaginatedResponse<RFQActivityLog>>> =>
    api.get('/rfq/activity-log/', { params: { rfq: rfqId, page_size: 200 } }),  // filters on `rfq`; `rfq_id` was ignored and returned every RFQ's activity

  // ==================== Convenience Methods ====================

  /**
   * List queries for an RFQ
   */
  listQueries: (rfqId: number): Promise<AxiosResponse<PaginatedResponse<RFQQuery>>> =>
    api.get('/rfq/queries/', { params: { rfq: rfqId, page_size: 200 } }),  // same: `rfq_id` is not a registered filter and was silently ignored

  /**
   * List responses for an RFQ
   */
  listResponses: (rfqId: number): Promise<AxiosResponse<PaginatedResponse<SupplierResponseListItem>>> =>
    api.get('/rfq/responses/', { params: { rfq: rfqId, page_size: 200 } }),   // the API filters on `rfq`; `rfq_id` was ignored and returned the first page of every response
};
